/* TimelineBar — horizontal scene tracklist for multi-scene reels/videos.
 *
 * Shows below the main canvas. Each scene is a card with:
 *   - tiny thumbnail (the scene's canvas_json rendered via Fabric)
 *   - scene index + duration badge
 *   - transition marker on the right edge (Cut | Fade | Slide)
 *   - hover: duplicate/delete actions
 *
 * Clicking a scene:
 *   1. Calls `onSwitch(newIdx)` — parent must serialize current canvas into
 *      scenes[activeIdx] first, then load scenes[newIdx].canvas_json.
 *
 * Props:
 *   scenes            : Array<{scene_id, canvas_json, animations, duration_ms, transition}>
 *   activeIdx         : number
 *   onSwitch(idx)     : user clicked a scene
 *   onAdd()           : add a new blank scene after `activeIdx`
 *   onDelete(idx)
 *   onDuplicate(idx)
 *   onReorder(from, to)
 *   onUpdateScene(idx, patch)   // e.g. { duration_ms: 2500 } or { transition: 'fade' }
 *   maxScenes         : number (default 5)
 */
import { useEffect, useRef, useState } from "react";
import * as fabric from "fabric";
import { Plus, Copy, Trash2, GripVertical } from "lucide-react";

const MAX_SCENES_DEFAULT = 5;

export default function TimelineBar({
  scenes,
  activeIdx,
  onSwitch,
  onAdd,
  onDelete,
  onDuplicate,
  onReorder,
  onUpdateScene,
  maxScenes = MAX_SCENES_DEFAULT,
  t = {},
}) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const totalMs = scenes.reduce((a, s) => a + (s.duration_ms || 2000), 0);

  return (
    <div className="ads-timeline" data-testid="ads-timeline">
      <div className="ads-timeline-header">
        <div className="ads-timeline-title" data-testid="ads-timeline-title">
          {t.studio_timeline_title || "Timeline"}
          <span className="ads-timeline-count" data-testid="ads-timeline-count">
            {scenes.length}/{maxScenes}
          </span>
          <span className="ads-timeline-total" data-testid="ads-timeline-total">
            {(totalMs / 1000).toFixed(1)}s
          </span>
        </div>
        <button
          type="button"
          onClick={onAdd}
          disabled={scenes.length >= maxScenes}
          className="ads-btn ads-btn-primary ads-timeline-add"
          data-testid="ads-timeline-add"
        >
          <Plus size={14} /> {t.studio_timeline_add || "Add scene"}
        </button>
      </div>

      <div className="ads-timeline-track">
        {scenes.map((s, idx) => (
          <SceneCard
            key={s.scene_id}
            scene={s}
            idx={idx}
            isActive={idx === activeIdx}
            isDragOver={overIdx === idx && dragIdx !== idx}
            canDelete={scenes.length > 1}
            onClick={() => onSwitch(idx)}
            onDelete={() => onDelete(idx)}
            onDuplicate={() => onDuplicate(idx)}
            onUpdate={(patch) => onUpdateScene(idx, patch)}
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
            onDragEnd={() => {
              if (dragIdx !== null && overIdx !== null && dragIdx !== overIdx) {
                onReorder(dragIdx, overIdx);
              }
              setDragIdx(null); setOverIdx(null);
            }}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

function SceneCard({
  scene, idx, isActive, isDragOver, canDelete,
  onClick, onDelete, onDuplicate, onUpdate,
  onDragStart, onDragOver, onDragEnd,
  t,
}) {
  const thumbRef = useRef(null);
  const [showDur, setShowDur] = useState(false);
  const [showTrans, setShowTrans] = useState(false);

  // Render a tiny preview of the scene's canvas_json into the thumbnail
  useEffect(() => {
    const el = thumbRef.current;
    if (!el) return;
    const cj = scene.canvas_json;
    const w = 96, h = (cj?.height || 628) / (cj?.width || 1200) * w;
    el.width = w; el.height = h || 54;
    const c = new fabric.StaticCanvas(el, {
      width: w, height: h || 54,
      backgroundColor: cj?.background || "#0b0b12",
    });
    // Scale objects down proportionally (fabric's loadFromJSON is heavy, so
    // we paint a simplified preview: background + first rect/text as the big
    // headline hint).
    const scale = w / (cj?.width || 1200);
    (cj?.objects || []).slice(0, 4).forEach((o) => {
      let fo = null;
      if (o.type === "textbox" || o.type === "text" || o.type === "i-text") {
        fo = new fabric.Text((o.text || "").slice(0, 14), {
          left: (o.left || 0) * scale,
          top: (o.top || 0) * scale,
          fontSize: Math.max(5, (o.fontSize || 24) * scale * 1.2),
          fill: o.fill || "#fff",
          fontWeight: o.fontWeight || "normal",
        });
      } else if (o.type === "rect") {
        fo = new fabric.Rect({
          left: (o.left || 0) * scale, top: (o.top || 0) * scale,
          width: (o.width || 10) * scale, height: (o.height || 10) * scale,
          fill: o.fill || "#333", rx: (o.rx || 0) * scale, ry: (o.ry || 0) * scale,
        });
      } else if (o.type === "circle") {
        fo = new fabric.Circle({
          left: (o.left || 0) * scale, top: (o.top || 0) * scale,
          radius: (o.radius || 10) * scale, fill: o.fill || "#333",
        });
      }
      if (fo) c.add(fo);
    });
    c.renderAll();
    return () => { try { c.dispose(); } catch {} };
  }, [scene.canvas_json]);

  const dur = scene.duration_ms || 2000;
  const trans = scene.transition || "cut";

  return (
    <div
      className={`ads-scene-card ${isActive ? "is-active" : ""} ${isDragOver ? "is-drag-over" : ""}`}
      data-testid={`ads-scene-card-${idx}`}
      draggable
      onClick={onClick}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <div className="ads-scene-idx" data-testid={`ads-scene-idx-${idx}`}>
        <GripVertical size={10} /> {idx + 1}
      </div>
      <canvas ref={thumbRef} className="ads-scene-thumb" />

      {/* Duration badge (click to edit) */}
      <button
        type="button"
        className="ads-scene-duration"
        onClick={(e) => { e.stopPropagation(); setShowDur(true); }}
        data-testid={`ads-scene-duration-${idx}`}
      >
        {(dur / 1000).toFixed(1)}s
      </button>
      {showDur && (
        <div className="ads-scene-popover" onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 10, color: "var(--ads-text-dim)", marginBottom: 4 }}>
            {t.studio_timeline_duration || "Duration (ms)"}
          </div>
          <input
            type="number"
            min={500}
            max={15000}
            step={100}
            value={dur}
            onChange={(e) => onUpdate({ duration_ms: Math.max(500, Math.min(15000, parseInt(e.target.value) || 2000)) })}
            className="ads-panel-input"
            data-testid={`ads-scene-duration-input-${idx}`}
            autoFocus
            onBlur={() => setShowDur(false)}
            onKeyDown={(e) => e.key === "Enter" && setShowDur(false)}
          />
        </div>
      )}

      {/* Transition marker (click to change) */}
      <button
        type="button"
        className={`ads-scene-trans ads-scene-trans-${trans}`}
        onClick={(e) => { e.stopPropagation(); setShowTrans(true); }}
        data-testid={`ads-scene-trans-${idx}`}
      >
        {trans}
      </button>
      {showTrans && (
        <div className="ads-scene-popover" onClick={(e) => e.stopPropagation()}>
          {["cut", "fade", "slide"].map((opt) => (
            <button
              key={opt}
              type="button"
              className={`ads-scene-trans-opt ${trans === opt ? "active" : ""}`}
              onClick={() => { onUpdate({ transition: opt }); setShowTrans(false); }}
              data-testid={`ads-scene-trans-opt-${idx}-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {/* Hover actions */}
      <div className="ads-scene-actions">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
          title={t.studio_timeline_duplicate || "Duplicate scene"}
          data-testid={`ads-scene-dup-${idx}`}
        >
          <Copy size={12} />
        </button>
        {canDelete && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title={t.studio_timeline_delete || "Delete scene"}
            data-testid={`ads-scene-del-${idx}`}
            style={{ color: "#f87171" }}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
